import { useMemo } from "react";
import { Layers, Users, IndianRupee } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  CompactStatCard,
  PageHeader,
  StatsGrid,
} from "@/components/shared";
import { useReferralNetwork, useReferralSummary } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { ReferralTeamTable } from "@/components/account/ReferralTeamShared";

const AccountLevelTeam = () => {
  const { data: summary, isLoading: summaryLoading } = useReferralSummary();
  const { data: network = [], isLoading: networkLoading } = useReferralNetwork();

  const levelTeam = useMemo(() => network.filter((r) => r.level > 1), [network]);

  const dataByLevel = useMemo(() => {
    const groups: Record<number, typeof levelTeam> = {};
    levelTeam.forEach((r) => {
      if (!groups[r.level]) groups[r.level] = [];
      groups[r.level].push(r);
    });
    return groups;
  }, [levelTeam]);

  const sortedLevels = useMemo(
    () =>
      Object.keys(dataByLevel)
        .map(Number)
        .sort((a, b) => a - b),
    [dataByLevel],
  );

  const levelSummaryRows = summary?.levelWiseSummary?.filter((l) => l.level > 1) ?? [];
  const levelCount =
    levelSummaryRows.reduce((sum, row) => sum + row.referralCount, 0) || levelTeam.length;
  const levelEarnings =
    levelSummaryRows.reduce((sum, row) => sum + row.totalEarnings, 0) ||
    levelTeam.reduce((sum, row) => sum + (row.commissionEarned || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Level Team"
        description="Your downline beyond direct referrals, grouped by level."
      />

      <StatsGrid columns={3}>
        <CompactStatCard
          title="Level Members"
          value={summaryLoading && networkLoading ? "…" : levelCount}
          icon={Users}
        />
        <CompactStatCard
          title="Levels"
          value={networkLoading ? "…" : sortedLevels.length}
          icon={Layers}
        />
        <CompactStatCard
          title="Level Earnings"
          value={
            summaryLoading && networkLoading ? "…" : `₹${Number(levelEarnings).toFixed(0)}`
          }
          icon={IndianRupee}
          iconClassName="bg-gradient-primary"
        />
      </StatsGrid>

      {networkLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : sortedLevels.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          No level team members yet. Levels fill as your direct team refers others.
        </Card>
      ) : (
        sortedLevels.map((lvl) => (
          <ReferralTeamTable
            key={lvl}
            title={`Level ${lvl}`}
            data={dataByLevel[lvl]}
            viewMode="cards"
            emptyMessage={`No members at level ${lvl}.`}
          />
        ))
      )}
    </div>
  );
};

export default AccountLevelTeam;
